
import React, { useEffect } from 'react';
import Header from '@/components/HeaderUpdated';
import Footer from '@/components/Footer';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { CheckCircle, ShoppingBag, Home } from 'lucide-react';
import { useCart } from '@/contexts/CartContext';
import { useLanguage } from '@/contexts/LanguageContext';

const OrderSuccess = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('orderId');
  const { clearCart } = useCart();
  
  useEffect(() => {
    // Clear the cart when the page is loaded
    clearCart();
    
    // If no order ID is present, redirect to the home page
    if (!orderId) {
      navigate('/');
    }
  }, [orderId, navigate, clearCart]);

  return (
    <div className="min-h-screen bg-brand-darker text-white flex flex-col">
      <Header />
      <main className="flex-grow flex items-center justify-center py-16 px-4">
        <div className="max-w-md w-full text-center">
          <div className="mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-500/20 mb-4">
              <CheckCircle size={40} className="text-green-500" />
            </div>
            <h1 className="text-3xl font-bold mb-2">{t('orderSuccess.title')}</h1>
            <p className="text-gray-400">
              {t('orderSuccess.orderNumber')} #{orderId?.substring(0, 8)} {t('orderSuccess.description')}
            </p>
          </div>
          
          <div className="bg-brand-gray p-6 rounded-lg mb-8">
            <h2 className="text-lg font-semibold mb-4">{t('orderSuccess.whatHappensNow')}</h2>
            <div className="space-y-4 text-left text-gray-300">
              <div className="flex items-start">
                <div className="bg-brand-red/20 rounded-full p-2 mr-3 mt-0.5">
                  <span className="text-brand-red font-bold">1</span>
                </div>
                <div>
                  <p>{t('orderSuccess.step1')}</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="bg-brand-red/20 rounded-full p-2 mr-3 mt-0.5">
                  <span className="text-brand-red font-bold">2</span>
                </div>
                <div>
                  <p>{t('orderSuccess.step2')}</p>
                </div>
              </div>
              <div className="flex items-start">
                <div className="bg-brand-red/20 rounded-full p-2 mr-3 mt-0.5">
                  <span className="text-brand-red font-bold">3</span>
                </div>
                <div>
                  <p>{t('orderSuccess.step3')}</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <Button
              variant="outline"
              className="bg-brand-gray border-gray-700 hover:bg-gray-700"
              asChild
            >
              <Link to="/my-account?tab=orders">
                <ShoppingBag size={18} className="mr-2" />
                {t('orderSuccess.myOrders')}
              </Link>
            </Button>
            <Button
              className="bg-brand-red hover:bg-brand-red/90"
              asChild
            >
              <Link to="/">
                <Home size={18} className="mr-2" />
                {t('orderSuccess.homePage')}
              </Link>
            </Button>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default OrderSuccess;
